import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { 
  FileText, 
  MapPin, 
  Clock,
  ArrowRight,
  Highlighter,
  MessageSquare
} from 'lucide-react';
import { SearchResult } from '@/hooks/useMultiInterviewSearch';

interface SearchResultsListProps {
  results: SearchResult[];
  searchQuery: string;
  onResultClick: (result: SearchResult) => void;
  onNavigateToInterview: (noteSpaceId: string, position: { x: number; y: number }) => void;
}

export const SearchResultsList = ({
  results,
  searchQuery,
  onResultClick,
  onNavigateToInterview
}: SearchResultsListProps) => {
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getTypeIcon = (type: string) => {
    const typeMap: { [key: string]: any } = {
      'pain': '😤',
      'insight': '💡',
      'quote': '💬',
      'question': '❓',
      'idea': '🚀',
      'emotion': '❤️',
      'note': '📝'
    };
    return typeMap[type.toLowerCase()] || '📝';
  };

  const truncateContent = (content: string, maxLength: number = 120) => {
    if (content.length <= maxLength) return content;
    return content.substr(0, maxLength) + '...';
  };

  if (results.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500 font-medium">No results found</p>
        <p className="text-xs text-gray-400 mt-1">
          Try different keywords or filters
        </p>
      </div>
    );
  }

  // Group results by interview
  const groupedResults = results.reduce((acc, result) => {
    const key = result.noteSpaceId;
    if (!acc[key]) {
      acc[key] = {
        noteSpaceName: result.noteSpaceName,
        results: []
      };
    }
    acc[key].results.push(result);
    return acc;
  }, {} as Record<string, { noteSpaceName: string; results: SearchResult[] }>);

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
            <Highlighter className="w-4 h-4 text-blue-500" />
            <span>Search Results</span>
          </h3>
          <Badge variant="outline" className="text-xs">
            {results.length} across {Object.keys(groupedResults).length} interviews
          </Badge>
        </div>

        {Object.entries(groupedResults).map(([noteSpaceId, group]) => (
          <div key={noteSpaceId} className="space-y-2">
            <div className="flex items-center space-x-2 py-2 border-b border-gray-100">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">{group.noteSpaceName}</span>
              <Badge variant="secondary" className="text-xs">
                {group.results.length} matches
              </Badge>
            </div>

            <div className="space-y-2">
              {group.results.map((result) => (
                <Card 
                  key={result.id} 
                  className="p-3 hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 hover:border-blue-300"
                  onClick={() => onResultClick(result)}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <span className="text-sm">{getTypeIcon(result.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <Badge 
                              variant="outline" 
                              className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                            >
                              {result.type || 'note'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigateToInterview(result.noteSpaceId, result.position);
                        }}
                        className="h-7 w-7 p-0 hover:bg-blue-50"
                      >
                        <ArrowRight className="w-3 h-3 text-blue-600" />
                      </Button>
                    </div>

                    <div className="text-sm text-gray-800 leading-relaxed">
                      {highlightText(truncateContent(result.content), searchQuery)}
                    </div>

                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>x:{Math.round(result.position.x)}, y:{Math.round(result.position.y)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>Click to view in context</span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};